import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { listUsers, deleteUser } from "../../actions/userActions";
import ListItemsDataTable from "../../components/crud/ListItemsDataTable.component";
import { Space, Button, notification, Typography, Tag } from 'antd';
import { 
  CheckCircleTwoTone, 
  CloseCircleTwoTone, 
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
  SafetyOutlined,
  StopOutlined,
  TeamOutlined
} from "@ant-design/icons";
import axios from "axios";
import TOTPDeactivateModal from "../../components/auth/TOTPDeactivateModal";
import PageWrapper from "../../components/common/PageWrapper";

const { Title, Text } = Typography;

export default function UserManagementScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userList = useSelector((state) => state.userList);
  const { loading, error, users, currentPage, totalPages, totalCount } = userList;

  const DEFAULT_LIMIT = 10;
  const DEFAULT_USER_FILTER = { query: "", page: 1, limit: DEFAULT_LIMIT, filter: "Email" };
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [api, contextHolder] = notification.useNotification();
  const [userListFilter, setUserListFilter] = useState(DEFAULT_USER_FILTER);
  const [usersData, setUsersData] = useState([]);
  const [sendingEmailConfirmationLink, setSendingEmailConfirmationLink] = useState(false);
  const [sendingEmailConfirmationLinkEmail, setSendingEmailConfirmationLinkEmail] = useState(null);
  const [userEmailConfirmationSentSucess, setUserEmailConfirmationSentSuccess] = useState(false);
  const [userEmailConfirmationSentError, setUserEmailConfirmationSentError] = useState(false);
  const [showTOTPDeactivateModal, setShowTOTPDeactivateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    setUsersData(Array.isArray(users) ? users : []);
  }, [users]);

  useEffect(() => {
    dispatch(listUsers(userListFilter));
  }, [dispatch, userListFilter]);

  const openNotification = (placement, description, message) => {
    api.info({
      message: message,
      description: description,
      placement,
      icon: (message === "Success") ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#ff0000" />,
    });
  };

  const handleDelete = (id) => {
    dispatch(deleteUser(id));
    setUserListFilter((prev) => ({ ...DEFAULT_USER_FILTER, limit: prev?.limit || limit }));
  };
  const handleUserFilterChange = (nextFilterOrUpdater) => {
    setUserListFilter((prevFilter) => {
      const nextFilter =
        typeof nextFilterOrUpdater === "function"
          ? nextFilterOrUpdater(prevFilter)
          : (nextFilterOrUpdater || prevFilter);

      const resolvedLimit = Number(nextFilter?.limit || prevFilter?.limit || DEFAULT_LIMIT);
      setLimit(resolvedLimit);

      return {
        ...prevFilter,
        ...nextFilter,
        page: Number(nextFilter?.page || prevFilter?.page || 1),
        limit: resolvedLimit,
      };
    });
  };


  const handleResendEmailConfirmationLink = (email) => {
    setSendingEmailConfirmationLink(true);
    setSendingEmailConfirmationLinkEmail(email);
    axios
      .post("/dotnet_api/v1/auth/resendemailconfirmationlink", { Email: email })
      .then((res) => {
        if (res.status === 200) {
          setUserEmailConfirmationSentSuccess(true);
          setUserEmailConfirmationSentError(false);
          setSendingEmailConfirmationLink(false);
          openNotification("bottom", "Email confirmation link sent successfully", "Success");
        }
      })
      .catch((err) => {
        setUserEmailConfirmationSentSuccess(false);
        setUserEmailConfirmationSentError(true);
        setSendingEmailConfirmationLink(false);
        openNotification("bottom", "Error sending email confirmation link", "Error");
      });
  };

  const handleTOTPDeactivate = (user) => {
    setSelectedUser(user);
    setShowTOTPDeactivateModal(true);
  };

  const handleTOTPDeactivateSuccess = () => {
    setShowTOTPDeactivateModal(false);
    setSelectedUser(null);
    dispatch(listUsers(userListFilter)); // Refresh the list
  };

  const handleTOTPDeactivateCancel = () => {
    setShowTOTPDeactivateModal(false);
    setSelectedUser(null);
  };

  const userDtoConfig = {
    UserName: { isSummaryProp: true },
    Email: { isSummaryProp: true },
    EmailConfirmed: { 
      isSummaryProp: true,
      render: (value) => value ? 
        <Tag color="green" icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}>Confirmed</Tag> : 
        <Tag color="red" icon={<CloseCircleTwoTone twoToneColor="#ff4d4f" />}>Not Confirmed</Tag>
    },
    Roles: { isSummaryProp: true },
    IsTOTPEnabled: { 
      isSummaryProp: true,
      render: (value) => value ? 
        <Tag color="blue" icon={<SafetyOutlined />}>TOTP Enabled</Tag> : 
        <Tag color="default">TOTP Disabled</Tag>
    },
  };


  return (
    <PageWrapper
      title="User Management"
      icon={<TeamOutlined style={{ fontSize: 24 }} />}
      headerActions={
        <Button
          type="primary"
          onClick={() => navigate("/settings/users/create")}
          icon={<PlusOutlined />}
          size="small"
        >
          Create User
        </Button>
      }
      showCard={true}
      cardProps={{
        styles: {
          body: { padding: '12px' }
        }
      }}
    >
      {contextHolder}
      <div style={{ margin: '-12px', padding: '12px' }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <div style={{ marginBottom: 8 }}>
            <Title level={4} style={{ margin: 0, color: '#1677ff' }}>
              Total Users: {totalCount ?? usersData.length}
            </Title>
          </div>
          <ListItemsDataTable
            loading={loading}
            dtoConfig={userDtoConfig}
            data={usersData}
            filter={userListFilter}
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount ?? usersData.length}
            limit={limit}
            onFilterChange={handleUserFilterChange}
            error={error}
            onRetry={() => dispatch(listUsers(userListFilter))}
            handleDeleteRow={handleDelete}
            handleEditForm={(id) => navigate(`/settings/users/${id}`)}
          />
        </Space>

        <TOTPDeactivateModal
          visible={showTOTPDeactivateModal}
          onCancel={handleTOTPDeactivateCancel}
          onSuccess={handleTOTPDeactivateSuccess}
          userEmail={selectedUser?.email}
        />
      </div>
    </PageWrapper>
  );
}